import React, { useRef, useState } from 'react';
import {
  Box,
  Avatar,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Fade,
  LinearProgress,
} from '@mui/material';
import {
  PhotoCamera,
  Delete,
  CloudUpload,
  Close,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useFileUpload, formatFileSize } from '../../../hooks/useFileUpload';
import { useProfile } from '../../../hooks/useProfile';
import { AvatarUploadResult } from '../../../types/profile';

interface AvatarUploaderProps {
  currentAvatarUrl?: string;
  userName: string;
  onUploadComplete?: (result: AvatarUploadResult) => void;
  onError?: (error: string) => void;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

const AVATAR_SIZES = {
  small: 64,
  medium: 120,
  large: 160,
} as const;

export const AvatarUploader: React.FC<AvatarUploaderProps> = ({
  currentAvatarUrl,
  userName,
  onUploadComplete,
  onError,
  size = 'medium',
  disabled = false,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  const { uploadAvatar, isUploadingAvatar, uploadProgress, avatarError, clearAvatarError } = useProfile();
  const { state: fileState, validateFile, generatePreview, clearPreview, clearError, resetState } = useFileUpload();
  
  const avatarSize = AVATAR_SIZES[size];

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    clearError();
    clearAvatarError();

    const isValid = await validateFile(file);
    if (!isValid) return;

    try {
      await generatePreview(file);
      setSelectedFile(file);
    } catch (error) {
      console.error('Failed to generate preview:', error);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const result = await uploadAvatar(selectedFile);
      onUploadComplete?.(result);
      
      // Reset state after successful upload
      setSelectedFile(null);
      clearPreview();
      resetState();
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'アップロードに失敗しました';
      onError?.(errorMessage);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    clearPreview();
    clearError();
    clearAvatarError();
    resetState();
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    // TODO: Implement avatar deletion API call
    console.log('Delete avatar functionality to be implemented');
  };

  const displayError = fileState.error?.message || avatarError;
  const isUploading = isUploadingAvatar || fileState.isUploading;
  const previewUrl = fileState.preview || currentAvatarUrl;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {/* Avatar Display Area */}
      <Box
        sx={{
          position: 'relative',
          borderRadius: '50%',
          overflow: 'hidden',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Avatar
          src={previewUrl}
          alt={userName}
          sx={{
            width: avatarSize,
            height: avatarSize,
            fontSize: avatarSize * 0.4,
            bgcolor: previewUrl ? 'transparent' : theme.palette.primary.main, // 画像がある場合は背景透明、ない場合は青背景
            cursor: disabled ? 'default' : 'pointer',
            transition: 'all 0.2s ease-in-out',
            // 枠線を追加
            border: `2px solid ${theme.palette.divider}`,
            ...(!disabled && {
              '&:hover': {
                transform: 'scale(1.05)',
                borderColor: theme.palette.primary.main,
              },
            }),
          }}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          {!previewUrl && userName.charAt(0).toUpperCase()}
        </Avatar>

        {/* Hover Overlay */}
        <Fade in={isHovered && !disabled && !isUploading}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              cursor: 'pointer',
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <PhotoCamera sx={{ color: 'white', fontSize: avatarSize * 0.3 }} />
          </Box>
        </Fade>

        {/* Upload Progress */}
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
            }}
          >
            <CircularProgress
              size={avatarSize * 0.3}
              sx={{ color: 'white' }}
              variant={uploadProgress ? 'determinate' : 'indeterminate'}
              value={uploadProgress?.percentage}
            />
          </Box>
        )}

        {/* Delete Button */}
        {currentAvatarUrl && !selectedFile && !disabled && (
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              bgcolor: theme.palette.error.main,
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.error.dark,
              },
            }}
            onClick={handleDelete}
          >
            <Delete fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* File Selection Info */}
      {selectedFile && (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            width: '100%',
            maxWidth: 300,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              選択されたファイル:
            </Typography>
            <IconButton
              size="small"
              onClick={handleCancel}
              sx={{ ml: 'auto' }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" fontWeight="medium">
            {selectedFile.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(selectedFile.size)}
          </Typography>

          {/* Upload Progress Bar */}
          {isUploading && uploadProgress && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress.percentage}
                sx={{ height: 4, borderRadius: 2 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {uploadProgress.percentage.toFixed(0)}% アップロード中...
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<CloudUpload />}
              onClick={handleUpload}
              disabled={isUploading}
              fullWidth
            >
              {isUploading ? 'アップロード中...' : 'アップロード'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCancel}
              disabled={isUploading}
            >
              キャンセル
            </Button>
          </Box>
        </Paper>
      )}

      {/* Upload Instructions */}
      {!selectedFile && !currentAvatarUrl && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          アバター画像をクリックして<br />
          プロフィール写真をアップロード
        </Typography>
      )}

      {/* Error Display */}
      {displayError && (
        <Alert 
          severity="error" 
          onClose={() => {
            clearError();
            clearAvatarError();
          }}
          sx={{ width: '100%', maxWidth: 300 }}
        >
          {displayError}
        </Alert>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled || isUploading}
      />
    </Box>
  );
}; 